'use client';

import { useRef } from 'react';

import {
  Turnstile,
  TurnstileInstance,
  TurnstileProps,
} from '@marsidev/react-turnstile';
import type { Control, FieldPath, FieldValues } from 'react-hook-form';
import { useController } from 'react-hook-form';

interface BaseCaptchaFieldProps {
  siteKey: string | undefined;
  options?: TurnstileProps;
  nonce?: string;
}

interface StandaloneCaptchaFieldProps extends BaseCaptchaFieldProps {
  onTokenChange: (token: string) => void;
  onInstanceChange?: (instance: TurnstileInstance | null) => void;
  control?: never;
  name?: never;
}

interface ReactHookFormCaptchaFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends BaseCaptchaFieldProps {
  control: Control<TFieldValues>;
  name: TName;
  onTokenChange?: never;
  onInstanceChange?: never;
}

type CaptchaFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> =
  | StandaloneCaptchaFieldProps
  | ReactHookFormCaptchaFieldProps<TFieldValues, TName>;

/**
 * @name CaptchaField
 * @description Self-contained captcha component with two modes:
 *
 * **Standalone mode** - For use outside react-hook-form:
 * ```tsx
 * <CaptchaField siteKey={siteKey} onTokenChange={setToken} />
 * ```
 *
 * **React Hook Form mode** - Automatic form integration:
 * ```tsx
 * <CaptchaField siteKey={siteKey} control={form.control} name="captchaToken" />
 * ```
 *
 * The two modes are split into separate components so that the
 * `useController` hook is always called unconditionally (mode is chosen by
 * props, which are stable for a given call site).
 */
export function CaptchaField<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(props: CaptchaFieldProps<TFieldValues, TName>) {
  if (props.control) {
    return (
      <ReactHookFormCaptchaField
        siteKey={props.siteKey}
        options={props.options}
        nonce={props.nonce}
        control={props.control}
        name={props.name}
      />
    );
  }

  return (
    <CaptchaWidget
      siteKey={props.siteKey}
      options={props.options}
      nonce={props.nonce}
      onToken={props.onTokenChange}
      onInstance={props.onInstanceChange}
    />
  );
}

function ReactHookFormCaptchaField<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(props: ReactHookFormCaptchaFieldProps<TFieldValues, TName>) {
  const { field } = useController({
    control: props.control,
    name: props.name,
  });

  return (
    <CaptchaWidget
      siteKey={props.siteKey}
      options={props.options}
      nonce={props.nonce}
      onToken={field.onChange}
    />
  );
}

interface CaptchaWidgetProps extends BaseCaptchaFieldProps {
  onToken: (token: string) => void;
  onInstance?: (instance: TurnstileInstance | null) => void;
}

function CaptchaWidget({
  siteKey,
  options,
  nonce,
  onToken,
  onInstance,
}: CaptchaWidgetProps) {
  const instanceRef = useRef<TurnstileInstance | null>(null);

  if (!siteKey) {
    return null;
  }

  const defaultOptions: Partial<TurnstileProps> = {
    options: {
      size: 'invisible',
    },
  };

  return (
    <Turnstile
      ref={(instance) => {
        if (instance) {
          instanceRef.current = instance;
          onInstance?.(instance);
        }
      }}
      siteKey={siteKey}
      onSuccess={onToken}
      scriptOptions={{
        nonce,
      }}
      {...defaultOptions}
      {...options}
    />
  );
}
